import { errorMessageFrom } from "../utils/formErrors";
import React, { useState, useEffect } from "react";
import {
  Box, Stack, Dialog, TextField, MenuItem, CircularProgress, IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import BtnSecondary from "./SecondaryButton";
import { DateField } from "./OnboardingFields";
import { useNotify } from "./NotificationProvider";
import { offboardEmployee } from "../services/employee";
import { tokens, displayFont } from "../theme";

import InlineMessage from "./InlineMessage";
const REASONS = ["Resignation", "Termination", "End of contract", "Retirement", "Layoff", "Other"];

/**
 * Offboarding dialog — captures last working day + reason (+ optional notes),
 * deactivates the employee, and persists the exit details. `onDone` fires on success.
 */
export default function OffboardDialog({ open, onClose, employee, onDone }) {
  const notify = useNotify();
  const [reason, setReason] = useState("");
  const [lastDay, setLastDay] = useState(null);
  const [dateOpen, setDateOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setReason(""); setLastDay(null); setNotes(""); setErrors({}); }
  }, [open]);

  if (!employee) return null;
  const name = `${employee.fname || ""} ${employee.lname || ""}`.trim() || "this employee";

  const submit = async () => {
    const e = {};
    if (!reason) e.reason = "Select a reason";
    if (!lastDay) e.lastDay = "Last working day is required";
    setErrors(e);
    if (Object.keys(e).length) return;

    setBusy(true);
    try {
      const res = await offboardEmployee(employee._id, {
        exitReason: reason,
        lastWorkingDay: lastDay,
        exitNotes: notes,
      });
      if (res?.success !== false) {
        notify.success(`${name} has been offboarded`);
        onDone?.();
        onClose();
      } else {
        notify.error(errorMessageFrom(res, "Could not offboard employee"));
      }
    } catch (err) {
      notify.error(err?.message || "Something went wrong offboarding the employee");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ sx: { borderRadius: "12px", width: "min(30rem,92vw)" } }}>
      <Box sx={{ p: "1.5rem" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb="1rem">
          <Stack direction="row" spacing={1.25} alignItems="center">
            <InlineMessage tone="danger" sx={{ width: "2.5rem", height: "2.5rem", alignItems: "center", justifyContent: "center" }}>
            <WarningAmberRoundedIcon />
          </InlineMessage>
            <Box>
              <Box sx={{ fontFamily: displayFont, fontSize: "1.25rem", fontWeight: 600, color: tokens.text }}>Offboard employee</Box>
              <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>{name}</Box>
            </Box>
          </Stack>
          <IconButton aria-label="Close" size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
        </Stack>

        <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mb: "1.25rem", lineHeight: 1.5 }}>
          This will deactivate the employee and revoke their access. You can reactivate them later if needed.
        </Box>

        <Box sx={{ fontSize: "0.8125rem", fontWeight: 500, color: tokens.textMuted, mb: "0.5rem" }}>
          Reason <span style={{ color: tokens.danger }}>*</span>
        </Box>
        <TextField select fullWidth size="small" value={reason} onChange={(e) => { setReason(e.target.value); setErrors((p) => ({ ...p, reason: "" })); }}
          error={!!errors.reason} helperText={errors.reason} sx={{ mb: "1rem" }}>
          {REASONS.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
        </TextField>

        <Box sx={{ "& > div": { width: "100% !important" } }}>
          <DateField
            label="Last working day *"
            value={lastDay}
            isDialogOpen={dateOpen}
            openDialog={() => setDateOpen(true)}
            closeDialog={() => setDateOpen(false)}
            validation={errors.lastDay}
            onSelect={(d) => { setLastDay(d); setErrors((p) => ({ ...p, lastDay: "" })); setDateOpen(false); }}
            fullwidth
          />
        </Box>

        <Box sx={{ fontSize: "0.8125rem", fontWeight: 500, color: tokens.textMuted, mt: "1rem", mb: "0.5rem" }}>
          Notes (optional)
        </Box>
        <TextField fullWidth size="small" multiline minRows={2} value={notes} placeholder="Handover notes, exit details…"
          onChange={(e) => setNotes(e.target.value)} sx={{ mb: "1.5rem" }} />

        <Stack direction="row" spacing={1.5} justifyContent="flex-end">
          <BtnSecondary onClick={onClose} sx={{ borderRadius: "8px" }}>Cancel</BtnSecondary>
          <BtnSecondary onClick={submit} disabled={busy}
            sx={{ height: "2.75rem", px: "1.5rem", borderRadius: "8px", color: tokens.danger,
                  borderColor: tokens.dangerBorder,
                  "&:hover": { borderColor: tokens.danger, background: tokens.dangerTint } }}>
            {busy ? <CircularProgress size={20} sx={{ color: "inherit" }} /> : "Offboard"}
          </BtnSecondary>
        </Stack>
      </Box>
    </Dialog>
  );
}
